import React, { useCallback, useMemo, useState } from 'react'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../Table/ActionableTable'
import { Action } from '../Table/RowActions'
import { useTranslation } from 'react-i18next'
import {
  CreateTranscript,
  CreateTranscriptRequestBody,
  PaginationResponse,
  Transcript,
  TranscriptOverview,
  TranscriptParchmentRequestStatus,
} from '../../swagger'
import { useNavigate } from 'react-router'
import { useTheme } from '@mui/material/styles'
import {
  Delete,
  Send,
  Visibility,
  Check,
  Cancel,
  FileCopy,
  Pause,
  PlayArrow,
} from '@mui/icons-material'
import { transcriptsApi } from '../../api/swagger'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import useEndpoint from '../../hooks/useEndpoint'
import ConfirmForParchmentModal from '../Modals/ConfirmForParchmentModal'
import { useAuth } from '../Routes/AuthProvider'
import { DataGridPropsWithoutDefaultValue } from '@mui/x-data-grid/models/props/DataGridProps'
import TranscriptConfirmDeleteModal from '../Modals/TranscriptConfirmDeleteModal'
import TranscriptConfirmMarkAsSentModal from '../Modals/TranscriptConfirmMarkAsSentModal'
import ConfirmWithButtonsModal from '../Modals/ConfirmWithButtonsModal'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import { TextButtonVariant } from '../Buttons/TextButton'
import DuplicateTranscriptModal from '../Modals/DuplicateTranscriptModal'
import cloneTranscript from '../../helpers/transcriptDuplication'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import { ACTING_AS_PARENT } from '../../utils/constants'
import useLoadingContext from '../../hooks/useLoadingContext'

export interface TranscriptsTableProps
  extends Pick<
    DataGridPropsWithoutDefaultValue,
    'onRowSelectionModelChange' | 'rowSelectionModel'
  > {
  transcripts: TranscriptOverview[]
  handlePaginationChange?: (pagination: PaginationResponse) => void
  pagination?: PaginationResponse
  ariaLabelledBy?: string
  canCreateTranscript: boolean
  triggerRefresh?: () => void
}

export const TranscriptsTable: React.FC<TranscriptsTableProps> = ({
  transcripts,
  handlePaginationChange,
  pagination,
  ariaLabelledBy,
  onRowSelectionModelChange,
  rowSelectionModel,
  canCreateTranscript,
  triggerRefresh,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const auth = useAuth()
  const {
    TranscriptFormCard: {
      sendTranscriptToParchment: sendTranscriptToParchmentLoadingId,
      markParchmentAsSent: markParchmentAsSentLoadingId,
      markParchmentAsUnsent: markParchmentAsUnsentLoadingId,
      createTranscript: createTranscriptLoadingId,
      updateTranscriptOnHoldStatus: updateOnHoldStatusLoadingId,
    },
    UseDeleteTranscript: { deleteTranscript: deleteTranscriptLoadingId },
    UseFetchTranscripts: { fetchTranscripts: fetchTranscriptLoadingId },
  } = useLoadingIds()

  const [transcriptKeyToSend, setTranscriptKeyToSend] = useState<
    number | undefined
  >()
  const [transcriptKeyToDelete, setTranscriptKeyToDelete] = useState<
    number | undefined
  >()
  const [transcriptToMarkAsSent, setTranscriptToMarkAsSent] = useState<
    TranscriptOverview | undefined
  >()
  const [transcriptKeyToMarkAsUnsent, setTranscriptKeyToMarkAsUnsent] =
    useState<number | undefined>()
  const [transcriptToToggleOnHold, setTranscriptToToggleOnHold] = useState<
    | {
        transcriptKey: number
        isOnHold: boolean
      }
    | undefined
  >()

  const [showConfirmParchmentModal, setShowConfirmParchmentModal] =
    useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [showConfirmMarkAsSentModal, setShowConfirmMarkAsSentModal] =
    useState(false)
  const [
    showConfirmRequestResendParchmentModal,
    setShowConfirmRequestResendParchmentModal,
  ] = useState(false)
  const [showConfirmMarkAsUnsentModal, setShowConfirmMarkAsUnsentModal] =
    useState(false)

  const [transcriptToDuplicate, setTranscriptToDuplicate] = useState<
    Transcript | undefined
  >()
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)

  const [pendingDuplicateKey, setPendingDuplicateKey] = useState<number | null>(
    null
  )
  const [pendingDuplicateName, setPendingDuplicateName] = useState<
    string | null
  >(null)

  const { handleMessage, handleError } = useAppSnackbar()

  const fetchTranscriptToDuplicate = async () => {
    try {
      if (pendingDuplicateKey == null) return
      const { transcript } = await transcriptsApi.fetchTranscript({
        transcriptKey: pendingDuplicateKey,
      })
      setTranscriptToDuplicate(transcript)
      setDuplicateModalOpen(true)
    } catch (error) {
      await handleError(
        error,
        t(
          'Transcripts.Table.Copy.Error',
          'Error while fetching the selected transcript'
        )
      )
    }
  }

  const {
    triggerFetch: triggerFetchTranscriptToDuplicate,
    isLoading: isLoadingFetchTranscript,
    addLoadingIds,
  } = useLoadingContext({
    loadingId: fetchTranscriptLoadingId,
    asyncFunction: fetchTranscriptToDuplicate,
  })

  const createTranscriptDuplicate = async () => {
    if (!transcriptToDuplicate || !pendingDuplicateName) return

    try {
      const createTranscriptBody: CreateTranscript = cloneTranscript(
        transcriptToDuplicate,
        pendingDuplicateName
      )

      const body: CreateTranscriptRequestBody = {
        transcript: {
          ...createTranscriptBody,
          studentDateOfBirth: createTranscriptBody.studentDateOfBirth,
          lastName: createTranscriptBody.lastName.replace(/^\s+|\s+$/g, ''),
        },
      }

      await transcriptsApi.createTranscript({ body })
      setDuplicateModalOpen(false)
      setTranscriptToDuplicate(undefined)
      setPendingDuplicateName(null)
      setPendingDuplicateKey(null)
      triggerRefresh?.()
      handleMessage(
        t(
          'Transcripts.Table.Duplicated.Success',
          'Transcript duplicated successfully!'
        )
      )
    } catch (error) {
      await handleError(
        error,
        t(
          'Transcripts.Table.Fetch.Transcript.Error',
          'Error duplicating transcript'
        )
      )
    }
  }

  const {
    triggerFetch: triggerCreateDuplicate,
    isLoading: isLoadingCreateDuplicate,
  } = useLoadingContext({
    loadingId: createTranscriptLoadingId,
    asyncFunction: createTranscriptDuplicate,
  })

  const handleDuplicate = useCallback(
    async (row: TranscriptOverview) => {
      if (auth.userDetails.actingAs !== ACTING_AS_PARENT) {
        handleMessage(
          t(
            'Transcripts.Table.Copy.NotAllowed',
            'You are not allowed to duplicate this transcript.'
          ),
          'error'
        )
        return
      }

      setDuplicateModalOpen(false)
      setTranscriptToDuplicate(undefined)
      setPendingDuplicateName(null)

      try {
        setPendingDuplicateKey(row.transcriptKey)
        triggerFetchTranscriptToDuplicate()
      } catch (error) {
        await handleError(
          error,
          t(
            'Transcripts.Table.Fetch.Transcript.Error',
            'Error fetching transcript data'
          )
        )
      }
    },
    [
      auth.userDetails.actingAs,
      t,
      handleMessage,
      handleError,
      triggerFetchTranscriptToDuplicate,
    ]
  )

  const handleConfirmDuplicate = async (newName: string) => {
    if (!transcriptToDuplicate) return
    setPendingDuplicateName(newName)
    triggerCreateDuplicate()
  }

  const canViewTranscript =
    auth.permissionAbility.can('view', 'Transcript') ||
    auth.userDetails.actingAs === ACTING_AS_PARENT

  const canPrintTranscript = auth.permissionAbility.can('print', 'Transcript')

  const hasParchmentAdminAbility = auth.permissionAbility.can(
    'parchmentAdmin',
    'Transcript'
  )

  useEndpoint({
    swaggerCall: () =>
      transcriptsApi.sendTranscriptToParchment({
        transcriptKey: transcriptKeyToSend as number,
      }),
    loadingId: sendTranscriptToParchmentLoadingId,
    successMessage: t(
      'Transcripts.TranscriptsTable.Actions.SendToParchmentSuccess',
      'Transcript has successfully been requested to send to parchment.'
    ),
    genericErrorMessage: t(
      'Transcripts.TranscriptsTable.Actions.SendToParchmentFail',
      'An unknown error occurred requesting to send transcript to parchment.'
    ),
    dontCall: () => !transcriptKeyToSend,
    failureCallback: (error, done) => {
      if ((error as Response).status === 400) {
        handleMessage(
          t(
            'Transcripts.TranscriptsTable.Actions.SendToParchmentFieldsNotMet',
            'One or more fields required by parchment have not been filled out. Please add the missing field(s) and try again.'
          ),
          'error'
        )

        done()
      }
    },
    successCallback: () => {
      triggerRefresh?.()
    },
  })

  useEndpoint({
    swaggerCall: () =>
      transcriptsApi.markParchmentAsUnsent({
        transcriptKey: transcriptKeyToMarkAsUnsent as number,
      }),
    loadingId: markParchmentAsUnsentLoadingId,
    successMessage: t(
      'Transcripts.TranscriptsTable.Actions.MarkAsUnsentSuccess',
      'Transcript has successfully been marked as unsent.'
    ),
    genericErrorMessage: t(
      'Transcripts.TranscriptsTable.Actions.MarkAsUnsentFailure',
      'An unknown error occurred requesting to mark a transcript as unsent.'
    ),
    dontCall: () => !transcriptKeyToMarkAsUnsent,
    successCallback: () => {
      triggerRefresh?.()
    },
  })

  useEndpoint({
    swaggerCall: () =>
      transcriptsApi.deleteTranscript({
        transcriptKey: transcriptKeyToDelete as number,
      }),
    loadingId: deleteTranscriptLoadingId,
    successMessage: t(
      'Transcripts.TranscriptsTable.Actions.DeleteTranscriptSuccess',
      'Transcript has successfully been deleted.'
    ),
    genericErrorMessage: t(
      'Transcripts.TranscriptsTable.Actions.DeleteTranscriptFailure',
      'An unknown error occurred requesting to delete transcript.'
    ),
    dontCall: () => !transcriptKeyToDelete,
    successCallback: () => {
      triggerRefresh?.()
    },
  })

  useEndpoint({
    swaggerCall: () =>
      transcriptsApi.markParchmentAsSent({
        body: {
          transcriptKeys: [transcriptToMarkAsSent?.transcriptKey as number],
        },
      }),
    loadingId: markParchmentAsSentLoadingId,
    successMessage: t(
      'Transcripts.TranscriptsTable.Actions.MarkAsSentSuccess',
      'Transcript requested status has successfully been cleared.'
    ),
    genericErrorMessage: t(
      'Transcripts.TranscriptsTable.Actions.DeleteTranscriptFailure',
      'An unknown error occurred when trying to clear transcript requested status.'
    ),
    dontCall: () => !transcriptToMarkAsSent?.transcriptKey,
    successCallback: () => {
      triggerRefresh?.()
    },
  })

  useEndpoint({
    swaggerCall: () =>
      transcriptsApi.updateTranscriptOnHoldStatus({
        body: {
          transcriptKey: transcriptToToggleOnHold?.transcriptKey as number,
          isOnHold: transcriptToToggleOnHold?.isOnHold as boolean,
        },
      }),
    loadingId: updateOnHoldStatusLoadingId,
    successMessage: t(
      'Transcripts.TranscriptsTable.Actions.UpdateOnHoldStatusSuccess',
      'Transcript on hold status has been updated successfully.'
    ),
    genericErrorMessage: t(
      'Transcripts.TranscriptsTable.Actions.UpdateOnHoldStatusFailure',
      'An unknown error occurred updating transcript on hold status.'
    ),
    dontCall: () => !transcriptToToggleOnHold,
    successCallback: () => {
      setTranscriptToToggleOnHold(undefined)
      triggerRefresh?.()
    },
  })

  const ActionableTableColumnsHeader: ActionableTableColumn[] = [
    {
      fieldName: 'transcriptKey',
      columnHeaderName: t('Transcripts.Table.Header.Id', 'Transcript Id'),
      sortable: true,
      align: 'left',
      headerAlign: 'left',
      sortingOrder: ['desc', 'asc'],
    },
    {
      fieldName: 'transcriptName',
      columnHeaderName: t('Transcripts.Table.Header.Name', 'Transcript Name'),
      sortable: true,
      align: 'left',
      headerAlign: 'left',
    },
    {
      fieldName: 'studentName',
      columnHeaderName: t('Transcripts.Table.Header.Child', 'Child Name'),
      sortable: true,
      align: 'left',
      headerAlign: 'left',
    },
    {
      fieldName: 'requestStatus',
      columnHeaderName: t(
        'Transcripts.Table.Header.RequestStatus',
        'Parchment Request Status'
      ),
      sortable: true,
      align: 'left',
      headerAlign: 'left',
    },
  ]

  // canViewTranscript
  const rowActions: Action<TranscriptOverview>[] = useMemo<
    Action<TranscriptOverview>[]
  >(() => {
    const viewTranscriptAction: Action<TranscriptOverview> = {
      actionName: t('Transcripts.Table.RowAction.View', 'View'),
      actionFunction: (row) => {
        if (!row?.transcriptKey) return
        navigate(`/transcripts/transcript-details/${row.transcriptKey}`)
      },
      actionKey: `view`,
      actionIcon: <Visibility style={{ minWidth: theme.spacing(4) }} />,
    }
    const sendToParchmentAction: Action<TranscriptOverview> = {
      actionName: t(
        'Transcripts.Table.RowAction.SendToParchment',
        'Send To Parchment'
      ),
      actionFunction: (row) => {
        setTranscriptKeyToSend(row?.transcriptKey)
        setShowConfirmParchmentModal(true)
      },
      actionKey: `send`,
      actionIcon: <Send style={{ minWidth: theme.spacing(4) }} />,
      hide: (row) =>
        row?.requestStatus === TranscriptParchmentRequestStatus.OnHold,
    }

    const deleteTranscriptAction: Action<TranscriptOverview> = {
      actionName: t('Transcripts.Table.RowAction.Delete', 'Delete'),
      actionFunction: (row) => {
        setTranscriptKeyToDelete(row?.transcriptKey)
        setShowConfirmDeleteModal(true)
      },
      actionKey: `delete`,
      actionIcon: <Delete style={{ minWidth: theme.spacing(4) }} />,
      hide: (row) =>
        row?.requestStatus === TranscriptParchmentRequestStatus.OnHold,
    }

    const markAsSentAction: Action<TranscriptOverview> = {
      actionName: t('Transcripts.Table.RowAction.MarkAsSent', 'Mark as sent'),
      actionFunction: (row) => {
        setTranscriptToMarkAsSent(row)
        setShowConfirmMarkAsSentModal(true)
      },
      actionKey: `markAsSent`,
      actionIcon: <Check style={{ minWidth: theme.spacing(4) }} />,
      hide: (row) =>
        !hasParchmentAdminAbility ||
        row?.requestStatus === TranscriptParchmentRequestStatus.OnHold,
    }

    const duplicateAction: Action<TranscriptOverview> = {
      actionName: t('Transcripts.Table.RowAction.Duplicate', 'Duplicate'),
      actionFunction: handleDuplicate,
      actionKey: 'duplicate',
      actionIcon: <FileCopy style={{ minWidth: theme.spacing(4) }} />,
      hide: (row) =>
        auth.userDetails.actingAs !== ACTING_AS_PARENT ||
        row?.requestStatus === TranscriptParchmentRequestStatus.OnHold,
    }

    const markTranscriptRequestedAgain: Action<TranscriptOverview> = {
      actionName: t(
        'Transcripts.Table.RowAction.ResendToParchment',
        'Request To Resend To Parchment'
      ),
      actionFunction: (row) => {
        setTranscriptKeyToSend(row.transcriptKey)
        setShowConfirmRequestResendParchmentModal(true)
      },
      actionKey: `markAsRequested`,
      actionIcon: <Send style={{ minWidth: theme.spacing(4) }} />,
      hide: (row) => {
        return (
          row?.requestStatus === TranscriptParchmentRequestStatus.Requested ||
          row?.requestStatus ===
            TranscriptParchmentRequestStatus.NotRequested ||
          row?.requestStatus === TranscriptParchmentRequestStatus.OnHold
        )
      },
    }

    const markAsUnsentAction: Action<TranscriptOverview> = {
      actionName: t(
        'Transcripts.Table.RowAction.ClearRequestedStatus',
        'Clear requested status'
      ),
      actionKey: 'clearRequestedStatus',
      actionIcon: <Cancel style={{ minWidth: theme.spacing(4) }} />,
      actionFunction: (row) => {
        setTranscriptKeyToMarkAsUnsent(row?.transcriptKey)
        setShowConfirmMarkAsUnsentModal(true)
      },
      hide: (row) =>
        row?.requestStatus !== TranscriptParchmentRequestStatus.Requested,
    }

    const putOnHoldAction: Action<TranscriptOverview> = {
      actionName: t('Transcripts.Table.RowAction.PutOnHold', 'Put on Hold'),
      actionKey: 'putOnHold',
      actionIcon: <Pause style={{ minWidth: theme.spacing(4) }} />,
      actionFunction: (row) => {
        setTranscriptToToggleOnHold({
          transcriptKey: row?.transcriptKey,
          isOnHold: true,
        })
        addLoadingIds([updateOnHoldStatusLoadingId])
      },
      hide: (row) =>
        !hasParchmentAdminAbility ||
        row?.requestStatus === TranscriptParchmentRequestStatus.OnHold,
    }

    const removeFromHoldAction: Action<TranscriptOverview> = {
      actionName: t(
        'Transcripts.Table.RowAction.RemoveFromHold',
        'Remove from Hold'
      ),
      actionKey: 'removeFromHold',
      actionIcon: <PlayArrow style={{ minWidth: theme.spacing(4) }} />,
      actionFunction: (row) => {
        setTranscriptToToggleOnHold({
          transcriptKey: row?.transcriptKey,
          isOnHold: false,
        })
        addLoadingIds([updateOnHoldStatusLoadingId])
      },
      hide: (row) =>
        !hasParchmentAdminAbility ||
        row?.requestStatus !== TranscriptParchmentRequestStatus.OnHold,
    }

    const actions = []
    if (canViewTranscript) {
      actions.push(viewTranscriptAction)
    }
    if (auth.userDetails.actingAs === ACTING_AS_PARENT) {
      actions.push(deleteTranscriptAction, sendToParchmentAction)
      actions.push(duplicateAction)
    }
    if (canPrintTranscript) {
      actions.push(markAsSentAction)
    }
    if (hasParchmentAdminAbility) {
      actions.push(markTranscriptRequestedAgain)
      actions.push(markAsUnsentAction)
      actions.push(putOnHoldAction)
      actions.push(removeFromHoldAction)
    }
    return actions
  }, [
    canViewTranscript,
    navigate,
    t,
    theme,
    auth.userDetails.actingAs,
    canPrintTranscript,
    hasParchmentAdminAbility,
    handleDuplicate,
    addLoadingIds,
    updateOnHoldStatusLoadingId,
  ])

  if (auth.userDetails.actingAs !== ACTING_AS_PARENT) {
    ActionableTableColumnsHeader.push({
      fieldName: 'parentName',
      columnHeaderName: t('Transcripts.Table.Header.ParentName', 'Parent Name'),
      sortable: true,
      align: 'left',
      headerAlign: 'left',
    })
    ActionableTableColumnsHeader.push({
      fieldName: 'parentUsername',
      columnHeaderName: t(
        'Transcripts.Table.Header.ParentEmail',
        'Parent Email'
      ),
      sortable: true,
      align: 'left',
      headerAlign: 'left',
    })
  }

  const hideConfirmResendParchmentModal = () => {
    setShowConfirmRequestResendParchmentModal(false)
  }

  const hideConfirmParchmentModal = () => {
    setShowConfirmParchmentModal(false)
  }

  const hideConfirmDeleteModal = () => {
    setShowConfirmDeleteModal(false)
  }

  const hideConfirmMarkAsSentModal = () => {
    setShowConfirmMarkAsSentModal(false)
  }

  const hideConfirmMarkAsUnsentModal = () => {
    setShowConfirmMarkAsUnsentModal(false)
  }

  const noResultsMessage = canCreateTranscript
    ? t(
        'Transcripts.Transcripts.Table.NoTranscripts.CanAdd',
        'You currently have no transcripts. Select the + to create one now!'
      )
    : t(
        'Transcripts.Transcripts.Table.NoTranscripts.CannotAdd',
        'You currently have no transcripts.'
      )

  return (
    <>
      <ConfirmForParchmentModal
        isOpen={showConfirmParchmentModal}
        onConfirm={() => {
          addLoadingIds([sendTranscriptToParchmentLoadingId])
          hideConfirmParchmentModal()
        }}
        onCancel={hideConfirmParchmentModal}
      />
      <ConfirmForParchmentModal
        isOpen={showConfirmRequestResendParchmentModal}
        title={t(
          'Transcripts.Modal.ConfirmResendToParchment.Title',
          'Request To Resend To Parchment'
        )}
        question={t(
          'Transcripts.Modal.ConfirmResendToParchment.Question',
          'Are you sure you want to resend this transcript to parchment?'
        )}
        onConfirm={() => {
          addLoadingIds([sendTranscriptToParchmentLoadingId])
          hideConfirmResendParchmentModal()
        }}
        onCancel={hideConfirmResendParchmentModal}
      />
      <TranscriptConfirmDeleteModal
        isOpen={showConfirmDeleteModal}
        onConfirm={() => {
          addLoadingIds([deleteTranscriptLoadingId])
          hideConfirmDeleteModal()
        }}
        onCancel={hideConfirmDeleteModal}
      />
      <TranscriptConfirmMarkAsSentModal
        isOpen={showConfirmMarkAsSentModal}
        onConfirm={() => {
          addLoadingIds([markParchmentAsSentLoadingId])
          hideConfirmMarkAsSentModal()
        }}
        onCancel={hideConfirmMarkAsSentModal}
        transcriptName={transcriptToMarkAsSent?.transcriptName ?? ''}
      />
      <ConfirmWithButtonsModal
        title={t(
          'Transcript.TranscriptFormCard.ClearRequestedStatus.Title',
          'Clear Requested Status'
        )}
        isOpen={showConfirmMarkAsUnsentModal}
        question={t(
          'Transcript.TranscriptFormCard.ClearRequestedStatusModal.Question',
          'Are you sure you want to clear the requested status of this transcript?'
        )}
        questionProps={{ variant: 'body1', align: 'center' }}
        confirmVariant={ContainedButtonVariant.YesImSure}
        cancelVariant={TextButtonVariant.NoCancel}
        onConfirm={() => {
          addLoadingIds([markParchmentAsUnsentLoadingId])
          hideConfirmMarkAsUnsentModal()
        }}
        onCancel={hideConfirmMarkAsUnsentModal}
      />
      <DuplicateTranscriptModal
        open={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false)
          setTranscriptToDuplicate(undefined)
          setPendingDuplicateKey(null)
          setPendingDuplicateName(null)
        }}
        onConfirm={handleConfirmDuplicate}
        defaultName={
          transcriptToDuplicate
            ? `${transcriptToDuplicate.transcriptName ?? ''} - ${t(
                'Transcripts.Table.Copy.Suffix',
                'copy'
              )}`
            : ''
        }
        loading={isLoadingFetchTranscript || isLoadingCreateDuplicate}
      />
      <ActionableTable
        checkboxSelection={
          transcripts.length > 0 &&
          auth.userDetails.actingAs !== ACTING_AS_PARENT
        }
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        ariaLabelledBy={ariaLabelledBy}
        columns={ActionableTableColumnsHeader}
        rows={transcripts}
        rowActions={rowActions.length ? rowActions : undefined}
        pagination={pagination}
        handlePaginationChange={handlePaginationChange}
        noResultsMessage={noResultsMessage}
        isRowSelectable={(params) =>
          params.row.requestStatus ===
          TranscriptParchmentRequestStatus.Requested
        }
      />
    </>
  )
}

export default TranscriptsTable
